import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import { tokens } from "../../../theme";
import { IconCheckCircle, IconAlert, IconClock } from "../../../components/ui/icons";
import { getServiceStatus } from "../../../apiRequests/post";

/**
 * Estado de los servicios de facturación de ARCA (ex AFIP).
 *
 * Es una pantalla de consulta rápida: se entra a saber si se puede facturar
 * ahora mismo. Por eso el estado se responde en una sola línea grande y todo
 * lo demás queda como respaldo debajo.
 */

/** Los tres estados posibles, cada uno con su tono del sistema. */
const TONOS = {
  ok: {
    ink: "var(--sup-state-ok-ink)",
    bg: "var(--sup-state-ok-bg)",
    icon: IconCheckCircle,
    titulo: "Servicios operativos",
    detalle: "La facturación electrónica de ARCA está respondiendo con normalidad.",
  },
  caido: {
    ink: "var(--sup-state-danger-ink)",
    bg: "var(--sup-state-danger-bg)",
    icon: IconAlert,
    titulo: "Servicios con problemas",
    detalle: "ARCA reporta una falla. Conviene esperar antes de emitir facturas.",
  },
  desconocido: {
    ink: "var(--sup-state-muted-ink)",
    bg: "var(--sup-state-muted-bg)",
    icon: IconClock,
    titulo: "Sin información",
    detalle: "No se pudo consultar el estado de los servicios.",
  },
};

const Fila = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      gap: "10px",
      flexWrap: "wrap",
      paddingY: "9px",
      borderTop: `1px solid ${tokens.borderSoft}`,
    }}
  >
    <Typography sx={{ fontSize: "12.5px", color: tokens.ink3, minWidth: 132 }}>{label}</Typography>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const ServiceStatus = () => {
  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchServiceStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getServiceStatus();
      setStatusData(response);
      setLastUpdate(moment());
    } catch (err) {
      setStatusData(null);
      setError(err.message || "No se pudo consultar el estado de los servicios.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceStatus();
  }, [fetchServiceStatus]);

  // `facturacion` trae "OK" o el texto de la falla, así que sirve de estado y
  // de mensaje a la vez.
  const facturacion = statusData?.facturacion;
  const tono = !statusData ? TONOS.desconocido : facturacion === "OK" ? TONOS.ok : TONOS.caido;
  const Icono = tono.icon;

  // Cuando ARCA falla, el propio texto de `facturacion` es la explicación.
  const detalleDeFalla = statusData && facturacion !== "OK" ? facturacion : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%", maxWidth: 720 }}>
      <Box
        sx={{
          backgroundColor: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: `${tokens.radiusCard}px`,
          padding: { xs: "16px", md: "18px 20px" },
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
              Facturación electrónica
            </Typography>
            <Typography sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
              Servicio de ARCA que usa el sistema para emitir comprobantes
            </Typography>
          </Box>
          <Button variant="outlined" onClick={fetchServiceStatus} disabled={loading}>
            {loading ? "Consultando…" : "Actualizar"}
          </Button>
        </Box>

        {/* El veredicto: lo único que hace falta leer para decidir si facturar. */}
        <Box
          sx={{
            backgroundColor: loading ? tokens.surface2 : tono.bg,
            borderRadius: `${tokens.radiusControl}px`,
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minHeight: 74,
          }}
        >
          {loading ? (
            <Typography sx={{ fontSize: "14px", color: tokens.ink3 }}>
              Consultando a ARCA…
            </Typography>
          ) : (
            <>
              <Box sx={{ color: tono.ink, display: "flex", flexShrink: 0 }}>
                <Icono size={24} />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                <Typography sx={{ fontSize: "16px", fontWeight: 600, color: tono.ink }}>
                  {tono.titulo}
                </Typography>
                <Typography sx={{ fontSize: "12.5px", color: tono.ink, opacity: 0.85 }}>
                  {tono.detalle}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {!loading && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {detalleDeFalla && (
              <Fila label="Reporta ARCA">
                <Typography sx={{ fontSize: "12.5px", color: tokens.ink, whiteSpace: "pre-line" }}>
                  {detalleDeFalla}
                </Typography>
              </Fila>
            )}

            {error && (
              <Fila label="Error de consulta">
                <Typography sx={{ fontSize: "12.5px", color: "var(--sup-state-danger-ink)" }}>
                  {error}
                </Typography>
              </Fila>
            )}

            {statusData?.rta && (
              <Fila label="Respuesta">
                <Typography
                  sx={{ fontSize: "12.5px", color: tokens.ink2, fontVariantNumeric: "tabular-nums" }}
                >
                  {statusData.rta}
                </Typography>
              </Fila>
            )}

            {statusData?.novedades && (
              <Fila label="Novedades">
                <Typography sx={{ fontSize: "12.5px", color: tokens.ink2, whiteSpace: "pre-line" }}>
                  {statusData.novedades}
                </Typography>
              </Fila>
            )}

            <Fila label="Última consulta">
              <Typography sx={{ fontSize: "12.5px", color: tokens.ink2 }}>
                {lastUpdate
                  ? `${lastUpdate.format("DD/MM/YY · HH:mm:ss")} (hace ${lastUpdate.fromNow(true)})`
                  : "—"}
              </Typography>
            </Fila>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ServiceStatus;
